import React, { useState, useEffect } from "react";
import { fetchWrapper } from "../utils/fetchWrapper";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { setPermissions } from "../redux/authSlice";
import {
  MdRefresh,
  MdSave,
  MdUndo,
  MdClear,
  MdPerson,
  MdFormatColorReset,
} from "react-icons/md";
import SearchableSelect from "./ui/SearchableSelect";
import "./ui/SearchableSelect.css";
import "./RoleAccess.css";

const MODULES = [
  "Dashboard",
  "Projects",
  "ProjectsViewOnly",
  "Leads",
  "LiveLeads",
  "BlockedIPs",
  "VisitorLogs",
  "Monitor",
  "Settings",
  "RoleAccess",
  "Analysis",
  "SEOAnalyzer",
  "Tools",
  "SystemLogs",
];

const ROLE_DEFAULTS = {
  ADMIN: {
    ...MODULES.reduce((acc, m) => ({ ...acc, [m]: true }), {}),
    ProjectsViewOnly: false,
    RoleAccess: true,
  },
  DEV: {
    ...MODULES.reduce((acc, m) => ({ ...acc, [m]: true }), {}),
    RoleAccess: false,
    Settings: false,
    Analysis: true,
    ProjectsViewOnly: false,
  },
  SDR: {
    ...MODULES.reduce((acc, m) => ({ ...acc, [m]: true }), {}),
    RoleAccess: false,
    Settings: false,
    Projects: false,
    Analysis: false,
    ProjectsViewOnly: false,
  },
};

const RoleAccess = () => {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOverrides, setUserOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Role Defaults");
  const [modifiedRoles, setModifiedRoles] = useState({});
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector((state) => state.auth);

  const modules = MODULES;

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await fetchWrapper.get("/permissions/roles");
      let data = res.data;

      const defaultRoles = ["ADMIN", "DEV", "SDR"];
      defaultRoles.forEach((roleName) => {
        const existingRole = data.find((r) => r.role === roleName);
        if (!existingRole) {
          data.push({
            role: roleName,
            modules: { ...ROLE_DEFAULTS[roleName] },
          });
        } else {
          // Ensure all modules are present by merging with defaults
          existingRole.modules = {
            ...ROLE_DEFAULTS[roleName],
            ...(existingRole.modules || {}),
          };
        }
      });

      setRoles(data);
      setModifiedRoles({});
    } catch (err) {
      console.error("Error fetching roles:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetchWrapper.get("/admin/users");
      const userData = res.data.data || res.data;
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchUserOverrides = async (userId) => {
    try {
      const res = await fetchWrapper.get(`/permissions/overrides/${userId}`);
      setUserOverrides(res.data);
    } catch (err) {
      console.error("Error fetching user overrides:", err);
      setUserOverrides({});
    }
  };

  const handleUserSelect = (e) => {
    const userId = e.target.value;
    const user = users.find((u) => u._id === userId);
    setSelectedUser(user);
    if (user) {
      fetchUserOverrides(userId);
    } else {
      setUserOverrides({});
    }
  };

  const handleToggle = (roleName, moduleName) => {
    const updatedRoles = [...roles];
    const roleIndex = updatedRoles.findIndex((r) => r.role === roleName);

    if (roleIndex > -1) {
      updatedRoles[roleIndex].modules[moduleName] =
        !updatedRoles[roleIndex].modules[moduleName];
      setRoles(updatedRoles);
      setModifiedRoles((prev) => ({ ...prev, [roleName]: true }));
    }
  };

  const handleOverrideToggle = (moduleName) => {
    setUserOverrides((prev) => ({
      ...prev,
      [moduleName]: !prev[moduleName],
    }));
  };

  const handleSave = async (roleName) => {
    const roleData = roles.find((r) => r.role === roleName);
    try {
      await fetchWrapper.put(`/permissions/roles/${roleName}`, {
        modules: roleData.modules,
      });
      setModifiedRoles((prev) => ({ ...prev, [roleName]: false }));
      toast.success(`Permissions for ${roleName} saved!`);

      if (currentUser?.role === roleName) {
        const res = await fetchWrapper.get(
          `/permissions/user/${currentUser.id}`,
        );
        dispatch(setPermissions(res.data));
      }
    } catch (err) {
      console.error("Error saving role:", err);
    }
  };

  const handleSaveOverrides = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await fetchWrapper.put(`/permissions/user/${selectedUser._id}`, {
        overrides: userOverrides,
      });
      toast.success(`Overrides for ${selectedUser.email} saved!`);

      if (currentUser?.id === selectedUser._id) {
        const res = await fetchWrapper.get(
          `/permissions/user/${currentUser.id}`,
        );
        dispatch(setPermissions(res.data));
      }
    } catch (err) {
      console.error("Error saving overrides:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleClearAll = (roleName) => {
    const updatedRoles = [...roles];
    const roleIndex = updatedRoles.findIndex((r) => r.role === roleName);
    if (roleIndex > -1) {
      const clearedModules = {};
      modules.forEach((m) => {
        clearedModules[m] = false;
      });
      updatedRoles[roleIndex].modules = clearedModules;
      setRoles(updatedRoles);
      setModifiedRoles((prev) => ({ ...prev, [roleName]: true }));
    }
  };

  const handleReset = (roleName) => {
    const updatedRoles = [...roles];
    const roleIndex = updatedRoles.findIndex((r) => r.role === roleName);
    if (roleIndex > -1) {
      updatedRoles[roleIndex].modules = { ...ROLE_DEFAULTS[roleName] };
      setRoles(updatedRoles);
      setModifiedRoles((prev) => ({ ...prev, [roleName]: true }));
    }
  };

  const getEffectiveStatus = (moduleName) => {
    if (!selectedUser) return "Unknown";
    const roleData = roles.find((r) => r.role === selectedUser.role);
    const roleDefault = roleData?.modules?.[moduleName] || false;
    const override = userOverrides[moduleName];

    const isEnabled =
      override !== undefined && override !== null ? override : roleDefault;
    return isEnabled ? "Granted" : "Denied";
  };

  return (
    <div className="role-access-page">
      <div className="header-section">
        <div className="title-area">
          <h3>
            <span className="icon">🛡️</span> Access Management
          </h3>
          <p>
            Configure module access defaults by role or set individual
            overrides.
          </p>
        </div>
        <button className="btn-refresh" onClick={fetchRoles}>
          <MdRefresh /> Refresh
        </button>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === "Role Defaults" ? "active" : ""}`}
          onClick={() => setActiveTab("Role Defaults")}
        >
          Role Defaults
        </button>
        <button
          className={`tab ${activeTab === "User Overrides" ? "active" : ""}`}
          onClick={() => setActiveTab("User Overrides")}
        >
          User Overrides
        </button>
      </div>

      {activeTab === "Role Defaults" ? (
        <div className="permission-table-container">
          <table className="permission-table">
            <thead>
              <tr>
                <th>MODULE / FEATURE</th>
                {["ADMIN", "DEV", "SDR"].map((role) => (
                  <th key={role}>
                    <div className="role-header">
                      <span>{role}</span>
                      <div className="action-buttons">
                        <button
                          className="btn-save"
                          onClick={() => handleSave(role)}
                          disabled={!modifiedRoles[role]}
                        >
                          <MdSave /> Save
                        </button>
                        <button
                          className="btn-reset"
                          onClick={() => handleReset(role)}
                        >
                          <MdUndo /> Reset
                        </button>
                        <button
                          className="btn-clear"
                          onClick={() => handleClearAll(role)}
                        >
                          <MdClear /> Clear
                        </button>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modules.map((moduleName) => (
                <tr key={moduleName}>
                  <td>{moduleName.replace(/([A-Z])/g, " $1").trim()}</td>
                  {["ADMIN", "DEV", "SDR"].map((roleName) => {
                    const roleData = roles.find((r) => r.role === roleName);
                    const isEnabled = roleData?.modules?.[moduleName] || false;
                    return (
                      <td key={roleName} className="text-center">
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={() => handleToggle(roleName, moduleName)}
                          />
                          <span className="slider round"></span>
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="user-overrides-container ">
          <div className="overrides-sidebar">
            <div className="selection-card px-4 py-3">
              <div className="card-header">
                <MdPerson className="person-icon" />
                <span>Select Employee</span>
              </div>
              <SearchableSelect
                value={selectedUser?._id || ""}
                onChange={handleUserSelect}
              >
                <option value="">Choose an employee...</option>
                {users
                  .filter((u) => u.role !== "SUPERADMIN")
                  .map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.email} ({user.role})
                    </option>
                  ))}
              </SearchableSelect>
              <button
                className="btn-save-premium my-2"
                onClick={handleSaveOverrides}
                disabled={!selectedUser || saving}
              >
                <MdSave /> {saving ? "Saving..." : "Save Overrides"}
              </button>
            </div>
          </div>

          <div className="overrides-main">
            <div className="permission-table-container">
              <table className="permission-table">
                <thead>
                  <tr>
                    <th>MODULE / FEATURE</th>
                    <th>ROLE DEFAULT</th>
                    <th>USER OVERRIDE</th>
                    <th className="text-center">EFFECTIVE STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map((moduleName) => {
                    const roleData = roles.find(
                      (r) => r.role === selectedUser?.role,
                    );
                    const roleDefault =
                      roleData?.modules?.[moduleName] || false;
                    const override = userOverrides[moduleName];
                    const effective = getEffectiveStatus(moduleName);

                    return (
                      <tr key={moduleName}>
                        <td>{moduleName.replace(/([A-Z])/g, " $1").trim()}</td>
                        <td className="text-muted">
                          {roleDefault ? "Enabled" : "Disabled"}
                        </td>
                        <td>
                          <div className="override-action-cell">
                            <label className="switch">
                              <input
                                type="checkbox"
                                disabled={!selectedUser}
                                checked={
                                  override !== undefined && override !== null
                                    ? override
                                    : roleDefault
                                }
                                onChange={() =>
                                  handleOverrideToggle(moduleName)
                                }
                              />
                              <span className="slider round"></span>
                            </label>
                            {override !== undefined && override !== null && (
                              <button
                                className="btn-clear-inline"
                                onClick={() => {
                                  setUserOverrides((prev) => {
                                    const next = { ...prev };
                                    delete next[moduleName];
                                    return next;
                                  });
                                }}
                                title="Reset to Role Default"
                              >
                                <MdFormatColorReset />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="text-center">
                          <span
                            className={`status-badge ${effective.toLowerCase()}`}
                          >
                            {effective}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleAccess;
